export const CAMERA_SAFE_ZONE_RATIO = 0.3;

const KEYBOARD_MOVES = {
  ArrowUp: [0, -1],
  ArrowRight: [1, 0],
  ArrowDown: [0, 1],
  ArrowLeft: [-1, 0],
};

const clamp = (value, minimum, maximum) => (
  Math.min(Math.max(Number.isFinite(value) ? value : minimum, minimum), maximum)
);

export function calculateCameraOffset({
  currentOffset,
  playerPosition,
  tileSize,
  viewportWidth,
  viewportHeight,
  safeZoneRatio = CAMERA_SAFE_ZONE_RATIO,
}) {
  const currentX = Number.isFinite(currentOffset?.x) ? currentOffset.x : 0;
  const currentY = Number.isFinite(currentOffset?.y) ? currentOffset.y : 0;
  if (viewportWidth <= 0 || viewportHeight <= 0 || tileSize <= 0) {
    return currentOffset ?? { x: currentX, y: currentY };
  }

  const ratio = Number.isFinite(safeZoneRatio)
    ? clamp(safeZoneRatio, 0, 1)
    : CAMERA_SAFE_ZONE_RATIO;
  const resolveAxis = (current, viewportPixels, playerTile) => {
    const safeZoneSize = viewportPixels * ratio;
    const safeZoneStart = (viewportPixels - safeZoneSize) / 2;
    const safeZoneEnd = safeZoneStart + safeZoneSize;
    const playerCenter = (playerTile + 0.5) * tileSize;
    const playerViewportPosition = playerCenter - current;
    let next = current;

    if (playerViewportPosition < safeZoneStart) {
      next = playerCenter - safeZoneStart;
    } else if (playerViewportPosition > safeZoneEnd) {
      next = playerCenter - safeZoneEnd;
    }
    return next;
  };

  const next = {
    x: resolveAxis(currentX, viewportWidth, playerPosition.x),
    y: resolveAxis(currentY, viewportHeight, playerPosition.y),
  };
  return next.x === currentX && next.y === currentY
    ? currentOffset ?? { x: currentX, y: currentY }
    : next;
}

export function calculateMapTransform({
  cameraOffset,
  mapWidth,
  mapHeight,
  tileSize,
  viewportWidth,
  viewportHeight,
  verticalScaleY = 1,
}) {
  const width = Math.max(Number.isFinite(mapWidth) ? mapWidth : 0, 0)
    * Math.max(Number.isFinite(tileSize) ? tileSize : 0, 0);
  const height = Math.max(Number.isFinite(mapHeight) ? mapHeight : 0, 0)
    * Math.max(Number.isFinite(tileSize) ? tileSize : 0, 0);
  const viewportPixelWidth = Math.max(
    Number.isFinite(viewportWidth) ? viewportWidth : 0,
    0,
  );
  const viewportPixelHeight = Math.max(
    Number.isFinite(viewportHeight) ? viewportHeight : 0,
    0,
  );
  const tiltScaleY = clamp(
    Number.isFinite(verticalScaleY) ? verticalScaleY : 1,
    0.1,
    1,
  );
  const visibleWorldHeight = viewportPixelHeight / tiltScaleY;
  const tiltOriginY = viewportPixelHeight / 2;
  const cameraX = Number.isFinite(cameraOffset?.x) ? cameraOffset.x : 0;
  const cameraY = Number.isFinite(cameraOffset?.y) ? cameraOffset.y : 0;

  return {
    cameraX,
    cameraY,
    height,
    tiltOriginY,
    tiltScaleY,
    translateX: -cameraX,
    translateY: tiltOriginY - (tiltOriginY / tiltScaleY) - cameraY,
    viewportHeight: viewportPixelHeight,
    viewportWidth: viewportPixelWidth,
    visibleWorldHeight,
    width,
  };
}

export function screenPointToTile({
  clientX,
  clientY,
  canvasRect,
  transform,
  mapWidth,
  mapHeight,
  tileSize,
}) {
  if (
    !Number.isFinite(clientX)
    || !Number.isFinite(clientY)
    || !canvasRect
    || canvasRect.width <= 0
    || canvasRect.height <= 0
    || tileSize <= 0
  ) return null;

  const canvasX = clientX - canvasRect.left;
  const canvasY = clientY - canvasRect.top;
  if (
    canvasX < 0
    || canvasY < 0
    || canvasX >= canvasRect.width
    || canvasY >= canvasRect.height
  ) return null;

  const logicalX = canvasX
    * ((transform?.viewportWidth || canvasRect.width) / canvasRect.width);
  const logicalY = canvasY
    * ((transform?.viewportHeight || canvasRect.height) / canvasRect.height);
  const tiltScaleY = clamp(
    Number.isFinite(transform?.tiltScaleY) ? transform.tiltScaleY : 1,
    0.1,
    1,
  );
  const tiltOriginY = Number.isFinite(transform?.tiltOriginY)
    ? transform.tiltOriginY
    : (transform?.viewportHeight || canvasRect.height) / 2;
  const worldX = logicalX - (transform?.translateX || 0);
  const worldY = tiltOriginY
    + ((logicalY - tiltOriginY) / tiltScaleY)
    - (transform?.translateY || 0);
  const x = Math.floor(worldX / tileSize);
  const y = Math.floor(worldY / tileSize);

  if (x < 0 || y < 0 || x >= mapWidth || y >= mapHeight) return null;
  return { worldX, worldY, x, y };
}

export function getTileBounds(x, y, tileSize) {
  if (!Number.isFinite(x) || !Number.isFinite(y) || tileSize <= 0) return null;
  return {
    bottom: (y + 1) * tileSize,
    height: tileSize,
    left: x * tileSize,
    right: (x + 1) * tileSize,
    top: y * tileSize,
    width: tileSize,
  };
}

export function getCenteredHitBounds({
  x,
  y,
  tileSize,
  width = tileSize,
  height = tileSize,
  minSize = 44,
}) {
  if (!getTileBounds(x, y, tileSize)) return null;
  const hitWidth = Math.max(Number.isFinite(width) ? width : 0, minSize);
  const hitHeight = Math.max(Number.isFinite(height) ? height : 0, minSize);
  const centerX = (x + 0.5) * tileSize;
  const centerY = (y + 0.5) * tileSize;
  return {
    bottom: centerY + (hitHeight / 2),
    height: hitHeight,
    left: centerX - (hitWidth / 2),
    right: centerX + (hitWidth / 2),
    top: centerY - (hitHeight / 2),
    width: hitWidth,
  };
}

export function isMapObjectVisuallyDisabled(object) {
  return object?.visuallyDisabled ?? Boolean(object?.disabled);
}

export function hitTestInteractiveObjects({ worldX, worldY, objects }) {
  if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) {
    return { consumed: false, disabled: false, target: null };
  }
  let best = null;
  (Array.isArray(objects) ? objects : []).forEach(target => {
    const bounds = target?.bounds;
    if (
      !bounds
      || worldX < bounds.left
      || worldX > bounds.right
      || worldY < bounds.top
      || worldY > bounds.bottom
    ) return;

    const priority = Number.isFinite(target.priority) ? target.priority : 0;
    const centerX = (bounds.left + bounds.right) / 2;
    const centerY = (bounds.top + bounds.bottom) / 2;
    const distance = ((worldX - centerX) ** 2) + ((worldY - centerY) ** 2);
    const depth = Number.isFinite(target.depth) ? target.depth : 0;
    if (
      !best
      || priority > best.priority
      || (priority === best.priority && distance < best.distance)
      || (
        priority === best.priority
        && distance === best.distance
        && depth > best.depth
      )
    ) {
      best = { depth, distance, priority, target };
    }
  });

  return best
    ? {
      consumed: true,
      disabled: Boolean(best.target.disabled),
      target: best.target,
    }
    : { consumed: false, disabled: false, target: null };
}

export function clampTilePosition(position, mapWidth, mapHeight) {
  const maxX = Math.max(Math.floor(mapWidth) - 1, 0);
  const maxY = Math.max(Math.floor(mapHeight) - 1, 0);
  return {
    x: clamp(Math.trunc(position?.x), 0, maxX),
    y: clamp(Math.trunc(position?.y), 0, maxY),
  };
}

export function moveKeyboardCursor(position, key, mapWidth, mapHeight) {
  const current = clampTilePosition(position, mapWidth, mapHeight);
  const [dx, dy] = KEYBOARD_MOVES[key] || [0, 0];
  return clampTilePosition(
    { x: current.x + dx, y: current.y + dy },
    mapWidth,
    mapHeight,
  );
}

export function filterMapItemsInViewport(
  items,
  { transform, tileSize, marginTiles = 3 },
) {
  if (!Array.isArray(items) || tileSize <= 0 || !transform) return [];
  const margin = Math.max(Number(marginTiles) || 0, 0) * tileSize;
  const tiltScaleY = clamp(
    Number.isFinite(transform.tiltScaleY) ? transform.tiltScaleY : 1,
    0.1,
    1,
  );
  const tiltOriginY = Number.isFinite(transform.tiltOriginY)
    ? transform.tiltOriginY
    : transform.viewportHeight / 2;
  const left = -transform.translateX - margin;
  const top = tiltOriginY
    - (tiltOriginY / tiltScaleY)
    - transform.translateY
    - margin;
  const right = left + transform.viewportWidth + (margin * 2);
  const bottom = tiltOriginY
    + ((transform.viewportHeight - tiltOriginY) / tiltScaleY)
    - transform.translateY
    + margin;

  return items.filter(item => {
    const legacyScale = Math.max(Number(item?.renderScaleTiles) || 1, 1);
    const width = Math.max(
      Number(item?.renderWidthTiles) || legacyScale,
      1,
    ) * tileSize;
    const height = Math.max(
      Number(item?.renderHeightTiles) || legacyScale,
      1,
    ) * tileSize;
    const centerX = (Number(item?.x) + 0.5) * tileSize;
    const centerY = (Number(item?.y) + 0.5) * tileSize;
    return centerX + (width / 2) >= left
      && centerX - (width / 2) <= right
      && centerY + (height / 2) >= top
      && centerY - (height / 2) <= bottom;
  });
}

export function encodeTilePalette(rows, resolveKey = tile => (
  tile?.base?.key || tile?.base || 'sand'
)) {
  const grid = Array.isArray(rows) ? rows : [];
  const height = grid.length;
  const width = height > 0 && Array.isArray(grid[0]) ? grid[0].length : 0;
  if (grid.some(row => !Array.isArray(row) || row.length !== width)) {
    throw new Error('Tile rows must form a rectangular grid.');
  }

  const tileKeys = grid.flatMap(row => row.map(tile => String(resolveKey(tile))));
  const keys = [...new Set(tileKeys)].sort();
  if (keys.length > 256) {
    throw new RangeError('A tile palette supports at most 256 terrain types.');
  }
  const indexByKey = new Map(keys.map((key, index) => [key, index]));
  return {
    data: Uint8Array.from(tileKeys, key => indexByKey.get(key)),
    height,
    indexByKey,
    keys,
    width,
  };
}
